import React, { useEffect } from 'react';
import SEO from '../../components/SEO';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import './PublicPages.css';
import './LegalPages.css';

export default function PrivacyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="public-page">
      <SEO
        title="Privacy Policy"
        description="Read Hangrily's privacy policy. Learn how we collect, use, and protect your data when you use our restaurant decision app."
        path="/privacy"
      />
      <PublicHeader />

      <main className="legal-content">
        <div className="legal-container">
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last Updated: July 28, 2026</p>

          <section className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              Welcome to Hangrily ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our web application and mobile applications (collectively, the "Service").
            </p>
            <p>
              By using Hangrily, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our Service.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Information We Collect</h2>
            
            <h3>2.1 Information You Provide</h3>
            <ul>
              <li><strong>Account Information:</strong> Email address, password (encrypted), name, and optional profile information</li>
              <li><strong>Partner Information:</strong> Email addresses of partners you link to your account</li>
              <li><strong>Preferences:</strong> Restaurant preferences including cuisine types, price ranges, and dietary preferences</li>
              <li><strong>Session Data:</strong> Your swipes, likes, and restaurant selections during decision sessions</li>
              <li><strong>Communications:</strong> Messages you send to our support team</li>
            </ul>

            <h3>2.2 Automatically Collected Information</h3>
            <ul>
              <li><strong>Approximate Location:</strong> City/region-level location when you use the restaurant search feature.</li>
              <li><strong>Precise Location:</strong> If you use our Cashback verification feature, we collect your device's precise GPS location at the moment you submit a visit, in order to confirm you were physically at the restaurant. This is only collected when you actively submit a cashback claim and is not tracked in the background.</li>
              <li><strong>Device Information:</strong> Browser type, operating system, device type, and screen resolution</li>
              <li><strong>Usage Data:</strong> Pages visited, features used, time spent on the Service, and interaction patterns</li>
              <li><strong>Cookies:</strong> Session cookies for authentication and preference storage</li>
            </ul>

            <h3>2.3 Camera and Photos</h3>
            <p>
              If you use our Cashback verification feature, our mobile app requests access to your camera or photo library so you can upload a photo of your receipt. Receipt photos are stored securely and used solely to verify and process your cashback claim.
            </p>

            <h3>2.4 Financial and Cashback Data</h3>
            <p>
              If you participate in our Cashback program, we collect and store bill amounts, cashback earnings, redemption history, and (for redemptions) the recipient email address used to deliver your payout. Redemptions may be processed through our third-party payout partner, Tremendous, who will receive the information necessary to deliver your reward.
            </p>

            <h3>2.5 Third-Party Data</h3>
            <ul>
              <li><strong>Restaurant Data:</strong> Restaurant information including names, addresses, ratings, and photos from third-party providers</li>
              <li><strong>Authentication:</strong> Data from Supabase for secure authentication</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. How We Use Your Information</h2>
            <p>We use the collected information for the following purposes:</p>
            <ul>
              <li><strong>Provide the Service:</strong> Enable restaurant decision-making, session management, and partner linking</li>
              <li><strong>Personalization:</strong> Remember your preferences and provide personalized restaurant recommendations</li>
              <li><strong>Analytics:</strong> Track your decision history, favorite restaurants, and usage statistics</li>
              <li><strong>Communication:</strong> Send important updates, security alerts, and respond to support requests</li>
              <li><strong>Improvement:</strong> Analyze usage patterns to improve features and user experience</li>
              <li><strong>Security:</strong> Detect and prevent fraud, abuse, and security incidents</li>
              <li><strong>Legal Compliance:</strong> Comply with applicable laws and regulations</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. Data Sharing and Disclosure</h2>
            
            <h3>4.1 We DO NOT Sell Your Data</h3>
            <p>
              We will never sell, rent, or trade your personal information to third parties for marketing purposes.
            </p>

            <h3>4.2 Limited Sharing</h3>
            <p>We may share your information only in the following circumstances:</p>
            <ul>
              <li><strong>With Your Partner:</strong> When you link a partner, they can see your shared session data and decision history</li>
              <li><strong>Service Providers:</strong> Supabase (database and authentication), restaurant data providers, Hostinger (hosting), and Tremendous (cashback reward payouts)</li>
              <li><strong>Legal Requirements:</strong> If required by law, court order, or government regulation</li>
              <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets (with notice to you)</li>
              <li><strong>With Your Consent:</strong> Any other sharing will require your explicit permission</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>5. Data Security</h2>
            <p>We implement industry-standard security measures to protect your information:</p>
            <ul>
              <li><strong>Encryption:</strong> All data is encrypted in transit (HTTPS/TLS) and at rest (AES-256)</li>
              <li><strong>Password Security:</strong> Passwords are hashed using bcrypt with salt</li>
              <li><strong>Access Controls:</strong> Strict role-based access to databases and systems</li>
              <li><strong>Regular Audits:</strong> Security assessments and vulnerability scanning</li>
              <li><strong>Secure Infrastructure:</strong> Hosted on secure, SOC 2 compliant platforms</li>
            </ul>
            <p>
              However, no method of transmission over the Internet is 100% secure. While we strive to protect your data, we cannot guarantee absolute security.
            </p>
          </section>

          <section className="legal-section">
            <h2>6. Data Retention</h2>
            <ul>
              <li><strong>Active Accounts:</strong> We retain your data as long as your account is active</li>
              <li><strong>Deleted Accounts:</strong> Data is permanently deleted within 30 days of account deletion</li>
              <li><strong>Session Data:</strong> Decision history is retained for analytics unless you delete your account</li>
              <li><strong>Backups:</strong> Backup copies are retained for 90 days for disaster recovery</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>7. Your Privacy Rights</h2>
            <p>You have the following rights regarding your personal information:</p>
            <ul>
              <li><strong>Access:</strong> Request a copy of all data we have about you</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your account and all associated data</li>
              <li><strong>Export:</strong> Download your data in a portable format (JSON)</li>
              <li><strong>Opt-Out:</strong> Unsubscribe from marketing emails (we don't send many anyway)</li>
              <li><strong>Restrict Processing:</strong> Limit how we use your data</li>
            </ul>
            <p>To exercise these rights, email us at <a href="mailto:privacy@hangrily.app">privacy@hangrily.app</a></p>
          </section>

          <section className="legal-section">
            <h2>8. Cookies and Tracking</h2>
            <p>We use the following types of cookies:</p>
            <ul>
              <li><strong>Essential Cookies:</strong> Required for authentication and core functionality</li>
              <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
              <li><strong>Analytics Cookies:</strong> Understand how you use the Service</li>
            </ul>
            <p>You can disable cookies in your browser settings, but this may limit functionality.</p>
          </section>

          <section className="legal-section">
            <h2>9. Children's Privacy</h2>
            <p>
              Hangrily is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us at <a href="mailto:privacy@hangrily.app">privacy@hangrily.app</a>, and we will delete such information.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. International Users</h2>
            <p>
              Hangrily is operated in the United States. If you are accessing the Service from outside the U.S., please be aware that your information may be transferred to, stored, and processed in the United States where our servers are located. By using the Service, you consent to this transfer.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Third-Party Links</h2>
            <p>
              Our Service may contain links to third-party websites (e.g., mapping services for directions). We are not responsible for the privacy practices of these external sites. We encourage you to read their privacy policies.
            </p>
          </section>

          <section className="legal-section">
            <h2>12. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material changes by:
            </p>
            <ul>
              <li>Posting the new Privacy Policy on this page</li>
              <li>Updating the "Last Updated" date</li>
              <li>Sending an email notification (for significant changes)</li>
            </ul>
            <p>
              Your continued use of the Service after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="legal-section">
            <h2>13. Contact Us</h2>
            <p>If you have questions or concerns about this Privacy Policy, please contact us:</p>
            <div className="contact-info">
              <p><strong>Email:</strong> <a href="mailto:privacy@hangrily.app">privacy@hangrily.app</a></p>
              <p><strong>Support:</strong> <a href="mailto:support@hangrily.app">support@hangrily.app</a></p>
              <p><strong>Website:</strong> <a href="https://hangrily.app">https://hangrily.app</a></p>
            </div>
          </section>

          <section className="legal-section">
            <h2>14. GDPR Compliance (EU Users)</h2>
            <p>If you are in the European Union, you have additional rights under GDPR:</p>
            <ul>
              <li><strong>Legal Basis:</strong> We process data based on consent, contract performance, and legitimate interests</li>
              <li><strong>Data Protection Officer:</strong> Contact <a href="mailto:dpo@hangrily.app">dpo@hangrily.app</a></li>
              <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
              <li><strong>Right to Lodge Complaint:</strong> File a complaint with your local data protection authority</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>15. California Privacy Rights (CCPA)</h2>
            <p>If you are a California resident, you have additional rights:</p>
            <ul>
              <li><strong>Right to Know:</strong> What personal information we collect and how we use it</li>
              <li><strong>Right to Delete:</strong> Request deletion of your personal information</li>
              <li><strong>Right to Opt-Out:</strong> Opt-out of the sale of personal information (we don't sell data)</li>
              <li><strong>Non-Discrimination:</strong> We will not discriminate against you for exercising your rights</li>
            </ul>
          </section>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
